import React from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../routes/paths";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useListLposQuery } from "../../stock/stockApi";
import { useDashboardPeriod } from "../useDashboardPeriod";
import { money, percentChange } from "../dashboardHelpers";
import DashboardSectionCard from "../components/DashboardSectionCard";

// Real LPO lifecycle (Batch42/44/91/98) - SUBMITTED and REVISED_SUBMITTED
// are folded into one "Pending Approval" row (both mean "awaiting an
// approver"); every other row is a distinct real status. There is no
// "Draft" stage in this system, unlike the original mockup.
const SUMMARY_ROWS = [
  { label: "Pending Approval", statuses: ["SUBMITTED", "REVISED_SUBMITTED"], cls: "text-warning" },
  { label: "Approved", statuses: ["APPROVED"], cls: "text-success" },
  { label: "Partially Received", statuses: ["PARTIALLY_RECEIVED"], cls: "text-info" },
  { label: "Fully Received", statuses: ["FULLY_RECEIVED"], cls: "text-success" },
  { label: "Rejected", statuses: ["REJECTED"], cls: "text-danger" },
  { label: "Cancelled", statuses: ["CANCELLED"], cls: "text-muted" },
];

// Driven by the global Period/Compare control (previously hardcoded to
// "This Month"). LPO's own real history is only a few days old as of this
// build (verified directly against TexasCentral - 2 rows total), so this
// will almost always show "No comparison data yet" for now - that's the
// correct, honest state per section 8, not a bug. The framework is the
// same one Collections uses; it becomes useful automatically as LPO
// volume accumulates, with no further changes needed here.
const LpoSummaryCard = () => {
  const canView = useHasPermission("Stock.ViewLpo");
  const { selectedBranchId } = useBranchFilter();
  const { period, comparison } = useDashboardPeriod();
  const branchId = selectedBranchId ?? undefined;
  const singleCompare = comparison?.type === "single" ? comparison : null;

  const { data, isFetching } = useListLposQuery(
    { branchId, dateFrom: period.dateFrom, dateTo: period.dateTo, pageSize: 500 },
    { skip: !canView }
  );
  const { data: compareResult } = useListLposQuery(
    singleCompare ? { branchId, dateFrom: singleCompare.dateFrom, dateTo: singleCompare.dateTo, pageSize: 500 } : undefined,
    { skip: !canView || !singleCompare }
  );

  if (!canView) return null;

  const items = data?.items || [];
  const rows = SUMMARY_ROWS.map((row) => ({ ...row, count: items.filter((l) => row.statuses.includes(l.status)).length }));
  const totalValue = items.reduce((s, l) => s + (Number(l.netAmount) || 0), 0);

  const compareItems = compareResult?.items || [];
  const compareValue = singleCompare ? compareItems.reduce((s, l) => s + (Number(l.netAmount) || 0), 0) : undefined;
  const change = singleCompare ? percentChange(totalValue, compareValue) : null;

  return (
    <div className="col-12 col-xl-4">
      <DashboardSectionCard title="LPO Summary" headerRight={period.label} isLoading={isFetching}>
        <ul className="list-unstyled mb-2">
          {rows.map((row) => (
            <li key={row.label} className="d-flex justify-content-between align-items-center mb-2">
              {/* Each row opens the LPO list on its own status for the same
                  period. The Pending row covers two statuses, so it uses the
                  list's "PENDING" pseudo-value rather than picking one. */}
              <Link
                to={paths.stockLposFiltered({
                  status: row.statuses.length > 1 ? "PENDING" : row.statuses[0],
                  dateFrom: period.dateFrom,
                  dateTo: period.dateTo,
                })}
                className="small text-decoration-none"
                title={`See ${row.label} LPOs for ${period.label}`}
              >
                {row.label}
              </Link>
              <span className={`fw-bold ${row.cls}`}>{row.count}</span>
            </li>
          ))}
        </ul>
        <hr className="my-2" />
        <div className="d-flex justify-content-between">
          <span className="fw-semibold">Total Value</span>
          <span className="fw-bold text-danger">{money(totalValue)}</span>
        </div>
        {singleCompare && change !== null && (
          <div className="small mt-1">
            <span className="text-muted">vs {singleCompare.label} </span>
            <span className={change >= 0 ? "text-success fw-semibold" : "text-danger fw-semibold"}>
              {change >= 0 ? "↑" : "↓"} {Math.abs(change).toFixed(1)}%
            </span>
          </div>
        )}
        {singleCompare && change === null && (
          <div className="small text-muted fst-italic mt-1">No comparison data yet</div>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default LpoSummaryCard;
